<template>
  <div class="w-full px-4 sm:px-6 lg:px-8">
    <div class="sm:flex sm:items-center">
      <div class="sm:flex-auto">
        <h1 class="text-xl font-semibold text-gray-900">Camera & Sign View</h1>
      </div>
    </div>

    <div class="mt-8 flex flex-col">
      <!-- Camera Selection -->
      <div class="mb-4 flex w-full flex-wrap items-center justify-between">
        <BaseInputSelect
          v-model="activeCameraId"
          :options="cameraOptions"
          class="w-full max-w-md"
          placeholder="Select a camera"
        />
      </div>

      <!-- Camera and Sign Display -->
      <div v-if="activeCamera" class="space-y-4">
        <!-- Desktop Layout (md and up) - Side by side -->
        <div class="hidden md:flex md:gap-4">
          <div class="flex-1">
            <div class="h-96 overflow-hidden rounded-lg border">
              <CameraStreamTab :camera="activeCamera" />
            </div>
          </div>

          <div
            v-if="activeCamera.signageIdentity && activeCamera.type !== 'exit'"
            class="flex-1 overflow-hidden rounded-lg border"
            :style="{
              aspectRatio:
                activeCamera.signageViewName === 'lady_margarets_park'
                  ? '16 / 9'
                  : '9 / 16',
            }"
          >
            <iframe
              :src="`/screen/?identity=${activeCamera.signageIdentity}`"
              class="scale-16 h-[625%] w-[625%] origin-top-left transform border-0"
              title="Site Signage"
              loading="lazy"
            />
          </div>
        </div>

        <!-- Mobile Layout (below md) - Stacked -->
        <div class="space-y-4 md:hidden">
          <!-- Barrier controls first for mobile -->
          <div v-if="activeCamera?.barrier">
            <BarrierControlItem :barrier="activeCamera.barrier" />
          </div>

          <!-- Camera second -->
          <div class="h-64 overflow-hidden rounded-lg border">
            <CameraStreamTab :camera="activeCamera" />
          </div>

          <!-- Sign third -->
          <div
            v-if="activeCamera.signageIdentity && activeCamera.type !== 'exit'"
            class="h-64 overflow-hidden rounded-lg border"
            :style="{
              aspectRatio:
                activeCamera.signageViewName === 'lady_margarets_park'
                  ? '16 / 9'
                  : '9 / 16',
            }"
          >
            <iframe
              :src="`/screen/?identity=${activeCamera.signageIdentity}`"
              class="scale-16 h-[625%] w-[625%] origin-top-left transform border-0"
              title="Site Signage"
              loading="lazy"
            />
          </div>
        </div>

        <!-- Desktop Controls - Positioned underneath for desktop layout -->
        <div v-if="activeCamera?.barrier" class="mt-4 hidden md:block">
          <BarrierControlItem :barrier="activeCamera.barrier" />
        </div>
      </div>

      <!-- No camera selected state -->
      <div v-else class="py-12 text-center">
        <IconHeroiconsSolidVideoCamera
          class="text-background-fg/40 mx-auto h-12 w-12"
        />
        <h3 class="text-background-fg mt-2 text-sm font-medium">
          No camera selected
        </h3>
        <p class="text-background-fg/60 mt-1 text-sm">
          Choose a camera from the dropdown above to view the live feed and
          signage.
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { type Camera } from "@/services/cameras/listCameras";

const { data: cameras } = useCameras();
const { data: sites } = useSites();

const activeCameraId = ref<string | null>(null);
const activeCamera = computed(() =>
  cameras.value?.find(({ id }) => id === activeCameraId.value),
);

const siteNameById = computed(() => {
  const map = new Map<string, string>();
  sites.value?.forEach((s) => map.set(s.id, s.name));
  return map;
});
// Prefix the site name only when cameras span multiple sites (All sites),
// otherwise the labels ("Entry"/"Exit") are ambiguous across sites.
const multiSite = computed(
  () => new Set((cameras.value ?? []).map((c) => c.siteId)).size > 1,
);
const cameraOptions = computed(
  () =>
    cameras.value?.map((camera) => ({
      value: camera.id,
      label:
        multiSite.value && camera.siteId
          ? `${siteNameById.value.get(camera.siteId) ?? "Unknown site"} — ${camera.displayName}`
          : camera.displayName,
    })) ?? [],
);
</script>
